import { useCallback, useRef, useState } from 'react'
import { CodeEditor } from './CodeEditor'
import { WysiwygEditor } from './WysiwygEditor'

interface DualEditorPanelProps {
  initialContent: string
}

export function DualEditorPanel({ initialContent }: DualEditorPanelProps) {
  const editorRef = useRef<any>(null)
  const codeMirrorRef = useRef<any>(null)
  const isUpdatingFromTinyMCE = useRef(false)
  const isUpdatingFromCodeMirror = useRef(false)

  const [codeValue, setCodeValue] = useState(initialContent)

  // Auto-indent CodeMirror content
  const indentCodeMirror = useCallback(() => {
    if (codeMirrorRef.current?.view) {
      const view = codeMirrorRef.current.view
      const doc = view.state.doc
      const lines = doc.lines

      // Auto-indent all lines
      view.dispatch({
        changes: Array.from({ length: lines }, (_, i) => {
          const line = doc.line(i + 1)
          return {
            from: line.from,
            to: line.to,
            insert: line.text.trim()
          }
        }).filter((change) => change.insert !== doc.lineAt(change.from).text)
      })
    }
  }, [])

  // Handle TinyMCE changes -> update CodeMirror
  const handleTinyMCEChange = useCallback(() => {
    if (editorRef.current && !isUpdatingFromCodeMirror.current) {
      isUpdatingFromTinyMCE.current = true
      const content = editorRef.current.getContent()
      setCodeValue(content)
      // Indent will happen after state update
      setTimeout(() => {
        indentCodeMirror()
        isUpdatingFromTinyMCE.current = false
      }, 0)
    }
  }, [indentCodeMirror])

  // Handle CodeMirror changes -> update TinyMCE
  const handleCodeMirrorChange = useCallback(
    (val: string) => {
      if (!isUpdatingFromTinyMCE.current && editorRef.current) {
        isUpdatingFromCodeMirror.current = true
        setCodeValue(val)
        editorRef.current.setContent(val)
        setTimeout(() => {
          indentCodeMirror()
          isUpdatingFromCodeMirror.current = false
        }, 0)
      }
    },
    [indentCodeMirror]
  )

  // Handle TinyMCE initialization
  const handleTinyMCEInit = useCallback(
    (_evt: any, editor: any) => {
      editorRef.current = editor
      // Set initial content in CodeMirror
      const content = editor.getContent()
      setCodeValue(content)
      setTimeout(() => {
        indentCodeMirror()
      }, 100)
    },
    [indentCodeMirror]
  )

  return (
    <section className="flex flex-col lg:flex-row gap-5 px-5 py-5 bg-[#dddddd] mb-9">
      <WysiwygEditor
        initialValue={initialContent}
        onInit={handleTinyMCEInit}
        onChange={handleTinyMCEChange}
      />
      <CodeEditor
        ref={codeMirrorRef}
        value={codeValue}
        onChange={handleCodeMirrorChange}
      />
    </section>
  )
}

